import { AppEnv, type FrontendOrg } from "@autumn/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useActiveSandbox } from "@/hooks/sandbox/useActiveSandbox";
import { clearCachedScopes } from "@/hooks/useScopes";
import {
	authClient,
	useListOrganizations,
	useSession,
} from "@/lib/auth-client";
import {
	clearLastSwitchedOrgId,
	getLastSwitchedOrgId,
	setActiveOrg,
	setLastSwitchedOrgId,
} from "@/lib/orgSync";
import { PERSIST_META } from "@/lib/queryPersistence";
import { useAxiosInstance } from "@/services/useAxiosInstance";
import { useEnv } from "@/utils/envUtils";
import { useActiveOrgId } from "./useActiveOrgId";

export {
	clearLastSwitchedOrgId,
	getLastSwitchedOrgId,
	setActiveOrg,
	setLastSwitchedOrgId,
};

export const useSwitchActiveOrg = () => {
	const { refetch: refetchSession } = useSession();

	return useCallback(
		async (orgId: string) => {
			await setActiveOrg(orgId);
			// The org query is keyed on activeOrgId, so refreshing the session
			// (which updates activeOrgId) is enough to refetch the new org. An extra
			// invalidate would force a redundant refetch and flash a skeleton.
			await refetchSession();
		},
		[refetchSession],
	);
};

export const useOrg = (params?: { env?: AppEnv; skipSandbox?: boolean }) => {
	const skipSandbox = params?.skipSandbox ?? true;
	const currentEnv = useEnv();
	const env = params?.env ?? currentEnv;
	const activeSandbox = useActiveSandbox();
	const axiosInstance = useAxiosInstance({ env: params?.env, skipSandbox });
	const { data: orgList, isPending: orgListLoading } = useListOrganizations();
	const { data: session } = useSession();
	const { activeOrgId } = useActiveOrgId();
	const sandboxOrgId =
		!skipSandbox && env === AppEnv.Sandbox ? (activeSandbox?.id ?? null) : null;

	const fetcher = async () => {
		const { data } = await axiosInstance.get("/organization");
		return data;
	};

	const {
		data: org,
		error,
		refetch,
	} = useQuery({
		queryKey: sandboxOrgId
			? ["org", env, activeOrgId, "sandbox", sandboxOrgId]
			: ["org", env, activeOrgId],
		queryFn: fetcher,
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: true,
		staleTime: 30_000,
		meta: PERSIST_META,
		// The app default is no retries; a failed org load blocks the whole dashboard, so keep retrying.
		retry: 3,
		enabled: !!activeOrgId,
	});
	const lastOrgId = getLastSwitchedOrgId();
	const rememberedOrgValid =
		orgListLoading || !orgList || orgList.some((o) => o.id === lastOrgId);
	const pendingOrgSwitch =
		!!lastOrgId &&
		!!activeOrgId &&
		lastOrgId !== activeOrgId &&
		rememberedOrgValid;

	const expectedOrgId = sandboxOrgId ?? activeOrgId;
	const orgIsReady =
		!!org && !!activeOrgId && org.id === expectedOrgId && !pendingOrgSwitch;
	const orgLoading = activeOrgId ? !orgIsReady : !session;

	useEffect(() => {
		const handleNoActiveOrg = async () => {
			if (!orgList || orgList.length === 0) {
				clearCachedScopes();
				await authClient.signOut();
				window.location.href = "/sign-in";
				return;
			}

			const lastOrgId = getLastSwitchedOrgId();
			const preferredOrg = lastOrgId
				? orgList.find((o) => o.id === lastOrgId)
				: null;
			const targetOrgId = preferredOrg?.id ?? orgList[0].id;

			await setActiveOrg(targetOrgId);
			window.location.reload();
		};

		const onImpersonateRoute = window.location.pathname.includes(
			"impersonate-redirect",
		);

		if (session && !activeOrgId && !orgListLoading && !onImpersonateRoute) {
			handleNoActiveOrg();
		}
	}, [activeOrgId, orgList, orgListLoading, session]);

	return {
		org: org as FrontendOrg,
		isLoading: orgLoading,
		error,
		mutate: refetch,
	};
};
